import { bool, object, string } from "yup";
import { emailRegex, onlyLetters, onlyNumbers } from "../regex/general";
import { activeSiteConfig } from "@config/site/site-config";
import type { Market } from "@config/site/types";
import { VALIDATION_RULES } from "@lib/constants/formValidations";
import { getDateOfBirthMarketConfig } from "@lib/utils/dateOfBirth";
import { phoneNumberRegex } from "../regex/phoneRegex";

export type DetailsFormErrorHeaders = {
    email?: string;
    enterPhone?: string;
    phoneRequirements?: string;
    firstName?: string;
    lastName?: string;
    gender?: string;
    policy?: string;
    birthdate?: string;
    requiredField?: string;
    maxNameCharacters?: string;
    maxEmailCharacters?: string;
    maxPhoneCharacters?: string;
    dateOfBirth?: string;
};

export function getContactDetailsValidationSchema(
    headers: DetailsFormErrorHeaders,
    labels: { email?: string; phone?: string }
) {
    const { country } = activeSiteConfig;

    return object().shape({
        email: string()
            .label(labels.email)
            .required()
            .matches(emailRegex, headers.email)
            .max(VALIDATION_RULES.email.maxCharacters, headers.maxEmailCharacters),
        phone: getPhoneNumberValidationSchema({
            headers,
            country: country as Market,
            stripSpecialChars: true,
            label: labels.phone,
        }),
        policy: bool().required(headers.policy).oneOf([true], headers.policy),
    });
}

export function getPersonalDetailsValidationSchema(
    headers: DetailsFormErrorHeaders,
    labels?: { firstName?: string; lastName?: string }
) {
    const { country } = activeSiteConfig;
    return object().shape({
        firstName: string()
            .label(labels?.firstName)
            .matches(onlyLetters, headers.firstName)
            .max(25, headers.maxNameCharacters)
            .required(),
        lastName: string()
            .label(labels?.lastName)
            .matches(onlyLetters, headers.lastName)
            .max(25, headers.maxNameCharacters)
            .required(),
        phoneticFirstName: string().nullable().max(25, headers.maxNameCharacters),
        phoneticLastName: string().nullable().max(25, headers.maxNameCharacters),
        gender: string(),
        Day: string()
            .min(1, "-")
            .max(2)
            .test("Day", headers?.dateOfBirth || "-", function (value) {
                if (!value) return true;
                const numValue = Number(value);
                return (numValue >= 1 && numValue <= 31) || false;
            }),
        Month: string()
            .min(1, "-")
            .max(2)
            .test("Month", headers?.dateOfBirth || "-", function (value) {
                if (!value) return true;
                const numValue = Number(value);
                return (numValue >= 1 && numValue <= 12) || false;
            }),
        Year: string()
            .min(4, "-")
            .max(4, "-")
            .test("Year", headers?.dateOfBirth || "-", function (value) {
                if (!value) return true;
                const numCode = Number(value);
                const currentYear = new Date().getFullYear();
                const minYear = currentYear - VALIDATION_RULES.dateOfBirth.maxAge;
                const isNotTooOld = numCode >= minYear;
                const isNotTooYoung = numCode <= currentYear;
                return isNotTooOld && isNotTooYoung;
            }),
        dateOfBirth: string().test("dateOfBirth", headers?.dateOfBirth, function () {
            const { Day, Month, Year } = this.parent;
            // Markets whose date of birth has no year (MM/DD or DD/MM) skip cross-field validation
            if (getDateOfBirthMarketConfig(activeSiteConfig.country)) return true;

            if (
                (Day && !Month) ||
                (Day && !Year) ||
                (Month && !Day) ||
                (Month && !Year) ||
                (Year && !Day) ||
                (Year && !Month)
            ) {
                return false;
            }
            return true;
        }),
        phone: string()
            .nullable()
            .notRequired()
            .when("$phone", {
                is: (val: string | null | undefined) => !!val,
                then: () =>
                    getPhoneNumberValidationSchema({
                        headers,
                        country: country as Market,
                    }),
                otherwise: (schema) => schema,
            }),
    });
}

export function getPhoneNumberValidationSchema({
    headers,
    country,
    stripSpecialChars = false,
    label,
}: {
    headers: DetailsFormErrorHeaders;
    country?: Market;
    /**
     * Strip whitespace, parentheses and hyphens from the phone number before validation.
     */
    stripSpecialChars?: boolean;
    label?: string;
}) {
    // Every market pattern is digit-only, so the separators shoppers group their digits with have
    // to go first. \s also covers the non-breaking spaces that autofilled and pasted numbers carry.
    const base = stripSpecialChars
        ? string().transform((value) => value.replace(/[\s()-]/g, ""))
        : string();

    const specificNumberFormat = phoneNumberRegex[country?.toLowerCase() as Market];

    if (specificNumberFormat) {
        return base
            .label(label)
            .matches(specificNumberFormat, headers.phoneRequirements)
            .required();
    }
    // No specific number format for market so we use the generic one
    return base
        .label(label)
        .matches(onlyNumbers, headers.phoneRequirements)
        .max(15, headers.maxPhoneCharacters)
        .required();
}
